import { useCallback, useEffect, useRef } from 'react'
import { components } from 'react-select'
import { Badge, Divider, Loader, Tooltip, Typography } from 'maple-storybook'
import { Close, FileDownloadOutlined, KeyboardArrowDown, Search } from 'maple-storybook/assets'

import { Button } from 'components'
import ItemGrid from 'components/Grid/ItemGrid'

export const checkAllOption = (array) => (Array.isArray(array) ? array.some((obj) => obj?.value === '*') : false)

export const ValueContainer = ({ children, ...props }) => {
  let values = children[0]

  let isDefaultLabel = props.selectProps.labelDefault

  if (props.isMulti && props.hasValue) {
    let childVals = children[0]
    let hasAllOption = checkAllOption(props.selectProps.value)
    let createSelect = props.selectProps.createSelect
    let defaultToShow = props.selectProps.defaultToShow
    let isCSV = props.selectProps.isCSV

    if (Array.isArray(childVals)) {
      values = isCSV ? (
        <Typography color="#667085" variant="text-sm" type="normal">
          Select CSV attributes
        </Typography>
      ) : (
        <>
          {!isDefaultLabel ? (
            createSelect ? (
              childVals.slice(0, defaultToShow)
            ) : (
              <Badge
                size="lg"
                variant="primary"
                label={hasAllOption ? 'All' : childVals[childVals.length - 1].props.data.label}
              />
            )
          ) : (
            <span>{props.selectProps.placeholder}&nbsp;</span>
          )}
          {(childVals.length > (createSelect ? defaultToShow : 1) || isDefaultLabel) && !hasAllOption && (
            <Tooltip
              variant="light"
              title={
                <ul style={{ paddingInlineStart: '20px', margin: 0 }}>
                  {childVals.map(
                    (value, index) => index !== childVals.length - 1 && <li key={index}>{value.props.data.label}</li>
                  )}
                </ul>
              }>
              <Badge
                className="ml-half"
                size="lg"
                variant={createSelect ? 'gray' : 'primary'}
                label={
                  <>
                    &nbsp;{!isDefaultLabel && '+'}
                    {hasAllOption
                      ? isDefaultLabel
                        ? childVals.length - 1
                        : childVals.length - 2
                      : isDefaultLabel
                        ? childVals.length
                        : childVals.length - (createSelect ? defaultToShow : 1)}
                  </>
                }
              />
            </Tooltip>
          )}
        </>
      )
    }
  }

  return (
    components.ValueContainer && (
      <components.ValueContainer {...props}>
        {!props.isMulti ? (
          <div className="d-flex align-items-center">
            {!!children && !props.isMulti && props.selectProps.searchIcon && (
              <Search className="searchIcon mr-half" fill="#667085" fontSize="1.3" />
            )}
            {isDefaultLabel && props.hasValue && props.selectProps.placeholder && (
              <span>{props.selectProps.placeholder}&nbsp;</span>
            )}
            {children}
          </div>
        ) : (
          <>
            {values}
            {children[1]}
          </>
        )}
      </components.ValueContainer>
    )
  )
}

const checkCurrentOption = (status, option) => status && option.props.value !== '*' && option.props.value

const getOptionTypes = (children, status) =>
  Array.isArray(children)
    ? children?.filter((option) =>
        checkCurrentOption(status ? option.props.isSelected : !option.props.isSelected, option)
      )
    : children

export const MenuList = ({ children, selectProps, ...rest }) => {
  if (!selectProps.optionAdditive && selectProps.isLoading && !Array.isArray(children)) {
    return (
      <ItemGrid className="d-flex flex-column align-items-center pd-0">
        <components.MenuList {...rest} selectProps={selectProps}>
          <Loader loaderBoxClasses="mt-half mb-half" />
        </components.MenuList>
      </ItemGrid>
    )
  }

  return Array.isArray(children) ? (
    <ItemGrid className="d-flex flex-column align-items-center pd-0">
      <components.MenuList {...rest}>
        {selectProps.allCheck && children[0]?.props?.label === 'All' && children[0]}
        {!selectProps.unSortedList ? (
          <>
            {rest.hasValue && getOptionTypes(children, true)}
            {getOptionTypes(children, false)}
          </>
        ) : (
          children
        )}
      </components.MenuList>
      {selectProps.isCSV && <Divider className="mt-half" />}
      {selectProps.isCSV && (
        <Button className="csv-button" variant="secondaryGray" onClick={selectProps.onCSVgeneration}>
          <FileDownloadOutlined className="mr-1" />
          <Typography color="gray-700" variant="text-sm" type="normal">
            {'Download CSV '}
          </Typography>
        </Button>
      )}
    </ItemGrid>
  ) : (
    <components.MenuList {...rest}>{children}</components.MenuList>
  )
}

export const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <Search className="searchIcon mr-half" fill="#667085" />
    </components.DropdownIndicator>
  )
}

export const MultiValue = (props) => {
  return (
    <components.MultiValueRemove {...props}>
      <div className="create-select-dropdown-label create-select-menu-item-styles">
        <Typography className="mr-quarter" type="medium" color="gray-700" variant="text-xs">
          {props.data.label}
        </Typography>
        <Close className="create-select-icon-styles" onClick={() => props.removeProps.onClick()} />
      </div>
    </components.MultiValueRemove>
  )
}

export const HideGroupHeading = (props) => {
  useEffect(() => {
    const groupContainer = document.querySelector(`#${props.id}`)?.parentElement?.parentElement
    if (groupContainer) {
      groupContainer.classList.add('collapsed-group')
    }
  }, [props.id])

  return (
    <div
      className="collapse-group-heading"
      onClick={() => {
        const groupContainer = document.querySelector(`#${props.id}`)?.parentElement?.parentElement
        if (groupContainer) {
          groupContainer.classList.toggle('collapsed-group')
        }
      }}>
      <KeyboardArrowDown className="searchIcon" fill="#667085" />
      <components.GroupHeading {...props} />
    </div>
  )
}

export const HideGroupMenuList = (props) => {
  let new_props = {
    ...props,
    children: Array.isArray(props.children)
      ? props.children.map((c, idx) => (idx === 0 ? c : { ...c, props: { ...c.props, className: 'collapsed-group' } }))
      : props.children
  }

  return Array.isArray(new_props.children) ? (
    <ItemGrid className="d-flex flex-column align-items-center pd-0">
      <components.MenuList {...new_props}>{props.children}</components.MenuList>
    </ItemGrid>
  ) : (
    <components.MenuList {...new_props} />
  )
}

export const useFetchOnMenuOpen = (fetchFn) => {
  const fetched = useRef(false)

  return useCallback(() => {
    if (!fetchFn) return
    if (fetched.current) return
    fetched.current = true
    fetchFn()
  }, [fetchFn])
}
